import './TicketDetail.less';

import clsx from 'clsx';
import { dispatch, useReframe } from 'nike-re-framejs';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';

import DelayedAppear from '@/common/components/DelayedAppear';
import EmptyState from 'nike-ui/src/components/empty-state/EmptyState';
import TicketIcon from '@/icons/TicketIcon';
import Actions from './Actions/Actions';
import { BarcodeHeader, SavedTicketHint } from './SavedTicketPart';
import SystemDetailNg from './SystemTicket/SystemDetailNg';
import TicketBody from './TicketBodyNg';
import { TicketDetailLoader } from './TicketDetailLoader';
import TicketFooter from './TicketFooter';
import TicketHeader from './TicketHeader';
import { trackVirtualPageView } from 'nike-ui/src/analytics/gtm';
import { navigate } from '@/routing';
import { MY_TICKETS } from '@/routing/routes';
import i18n from '@i18n';
import { cashinClosingModeActive$, evalEventualTotals$ } from 'nike-ui/src/components/ticketus/subs';
import { CashinPosition } from 'nike-ui/src/components/turboLive/components/cashin/constants';
import CashinPositionContext from 'nike-ui/src/common/CashinPositionContext';

function EmptyTicket() {
    const stateIcon = <TicketIcon width={60} height={60} fill={'#aab2bd'} />;
    return (
        <DelayedAppear delay={5000} loader={TicketDetailLoader}>
            <EmptyState stateIcon={stateIcon} text={i18n('ticketus.ticket.error.notfound.text')} />
        </DelayedAppear>
    );
}

export const SavedTicketsHazardText = ({ detail }) => (
    <div className={clsx('px-20 fs-14', { 'pb-30': detail, 'pt-20': !detail })}>
        <span className="c-black-50">{i18n('saved.tickets.information.article')}</span>
        <a className="saved-tickets-link c-link-default pl-5" onClick={() => navigate(MY_TICKETS)}>
            {i18n('saved.tickets.information.tickets')}
        </a>
        <span className="c-black-50"> {i18n('saved.tickets.information.ending')}</span>
    </div>
);

// sluzi na derefnutie ticketu z reframu
export const TicketDetailSubscriber = React.memo(function TicketDetailSubscriber({ hash, isActive }) {
    const derefSub = useReframe();
    const ticket = derefSub(['ticketus/detailOrOverview/ticket', hash]);

    React.useEffect(
        function subscribeTicket() {
            if (isActive) {
                dispatch(['ticketus/detail/subscribe', [hash]]);
            }
        },
        [isActive, hash]
    );

    if (!ticket || ticket.get('error')) {
        return <EmptyTicket />;
    }

    return (
        <CashinPositionContext.Provider value={{ cashinPosition: CashinPosition.TICKET_DETAIL }}>
            <TicketDetail ticket={ticket} isActive={isActive} />
        </CashinPositionContext.Provider>
    );
});

TicketDetailSubscriber.propTypes = {
    hash: PropTypes.string.isRequired,
    isActive: PropTypes.bool.isRequired
};

function TicketDetail({ ticket, isActive = false, renderWithTicketHeader = true }) {
    const saved = ticket.get('source') === 'Saved';
    useTicketDetailLog(ticket);
    const { subscribe } = useReframe();
    const cashinClosingModeActive = subscribe(cashinClosingModeActive$(ticket.get('ticketId')));
    const eventualTotals = subscribe(evalEventualTotals$(ticket.get('ticketId')));

    return (
        <div className={clsx('tickets-detail layout-detail-rw', { 'inactive-swipeable-item': !isActive })}>
            {renderWithTicketHeader ? <TicketHeader ticket={ticket.toJS()} /> : null}
            <div className="ticket-body">
                <TicketBody
                    isActive={isActive}
                    ticket={ticket}
                    cashinClosingModeActive={cashinClosingModeActive}
                    eventualTotals={eventualTotals}
                />
                <TicketFooter
                    ticket={ticket}
                    winningMethod={ticket.get('winningMethod')}
                    status={ticket.get('status')}
                    cashinClosingModeActive={cashinClosingModeActive}
                />
            </div>
            {saved ? (
                <div className="saved-ticket">
                    {ticket.get('source') === 'Saved' && <SavedTicketsHazardText detail />}
                    <BarcodeHeader barcode={ticket.get('ticketNoVisual')} />
                    <SavedTicketHint />
                </div>
            ) : null}
            {cashinClosingModeActive ? null : <Actions ticket={ticket.toJS()} />}
            <SystemDetailNg ticket={ticket} isActive={isActive} />
        </div>
    );
}

function useTicketDetailLog(ticket) {
    const _isSavedTicket = ticket.get('source') === 'Saved';
    const hash = ticket.get('hash');

    useEffect(() => {
        if (_isSavedTicket && hash) {
            trackVirtualPageView(`ulozeny-tiket/${hash}`, `Detail uloženého tiketu ${hash}`);
        }
    }, [_isSavedTicket, hash]);
}

export default TicketDetail;
